import QRCode from 'qrcode'
import { useEffect, useRef, useState } from 'react'

import {
  getModuleCount,
  QUIET_ZONE,
  RENDER_OPTIONS,
  TARGET_SIZE
} from '../utils/payBySquare'

/**
 * Re-renders on monitor changes, otherwise a canvas sized for a retina display
 * stays blurry after the window moves to a 1x screen (and vice versa).
 */
function useDevicePixelRatio() {
  const [dpr, setDpr] = useState(() => window.devicePixelRatio || 1)

  useEffect(() => {
    const media = window.matchMedia(`(resolution: ${dpr}dppx)`)
    const onChange = () => setDpr(window.devicePixelRatio || 1)
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [dpr])

  return dpr
}

interface PayBySquareQRProps {
  /** The pay-by-square payload. Renders nothing when empty. */
  value: string
  className?: string
}

const PayBySquareQR = ({ value, className }: PayBySquareQRProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const dpr = useDevicePixelRatio()

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !value) return

    const edge = getModuleCount(value) + 2 * QUIET_ZONE
    // Whole device pixels per module. A fractional module size is what makes a
    // dense pay-by-square code look smeared and trips up banking app scanners.
    const scale = Math.max(1, Math.round((TARGET_SIZE * dpr) / edge))

    let cancelled = false
    QRCode.toCanvas(canvas, value, { ...RENDER_OPTIONS, scale })
      .then(() => {
        if (cancelled) return
        // toCanvas forces the CSS size to the raw pixel size; scale it back down
        // so one module still maps to a whole number of device pixels.
        const cssSize = `${(edge * scale) / dpr}px`
        canvas.style.width = cssSize
        canvas.style.height = cssSize
      })
      .catch((error) => {
        console.error('[PayBySquareQR] render failed:', error)
      })

    return () => {
      cancelled = true
    }
  }, [value, dpr])

  if (!value) return null

  return <canvas ref={canvasRef} className={className} />
}

export default PayBySquareQR
